import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthProvider';
import AdminTenantAPI from '../../api/admin/tenants.js';
import './AdminLayout.css';

/**
 * Admin Layout Component
 * Provides common layout structure for all admin pages
 * 
 * Created: July 9, 2025
 * Purpose: Consistent navigation, authentication, and layout for admin portal
 */
const AdminLayout = ({ children }) => {
  // Use admin auth context
  const { adminUser, isAuthenticated, isLoading: authLoading, hasPermission, logout: contextLogout } = useAdminAuth();

  const [impersonationStatus, setImpersonationStatus] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Authentication check
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Check impersonation status
  useEffect(() => {
    if (isAuthenticated) {
      checkImpersonationStatus();
    }
  }, [isAuthenticated]);

  const checkImpersonationStatus = () => {
    console.log('AdminLayout: Checking impersonation status...');
    const status = AdminTenantAPI.getImpersonationStatus();
    console.log('AdminLayout: Impersonation status result:', status);
    setImpersonationStatus(status);
    
    // Debug localStorage contents - check both admin and impersonation tokens
    const adminToken = localStorage.getItem('admin_token');
    const adminUser = localStorage.getItem('admin_user');
    const impersonationToken = localStorage.getItem('impersonation_token');
    const impersonationUser = localStorage.getItem('impersonation_user');

    console.log('AdminLayout: localStorage debug:', {
      adminToken: adminToken ? 'Present' : 'Missing',
      adminUser: adminUser ? 'Present' : 'Missing',
      impersonationToken: impersonationToken ? 'Present' : 'Missing',
      impersonationUser: impersonationUser ? 'Present' : 'Missing',
      isImpersonating: !!impersonationToken
    });
  };

  const handleLogout = async () => {
    try {
      await contextLogout();
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/admin/login');
    }
  };

  const handleStopImpersonation = async () => {
    try {
      await AdminTenantAPI.stopImpersonation();
      setImpersonationStatus(null);
      // Refresh the page to reflect changes
      window.location.reload();
    } catch (error) {
      console.error('Stop impersonation error:', error);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const isActivePage = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  if (authLoading) {
    return (
      <div className="admin-layout-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading admin portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      {/* Impersonation Banner */}
      {impersonationStatus && (
        <div className="impersonation-banner">
          <div className="banner-content">
            <span className="banner-icon">👤</span>
            <div className="banner-text">
              <strong>Impersonating:</strong> {impersonationStatus.user.name} ({impersonationStatus.user.email})
              <span className="banner-time">
                Started: {new Date(impersonationStatus.started_at).toLocaleString()}
              </span>
            </div>
            <button onClick={handleStopImpersonation} className="stop-impersonation-btn">
              Stop Impersonation
            </button>
          </div>
        </div>
      )}

      {/* Top Header */}
      <header className="admin-header">
        <div className="header-left">
          <button onClick={toggleSidebar} className="sidebar-toggle">
            <span className="toggle-icon">{isSidebarOpen ? '⬅' : '➡'}</span>
          </button>
          <h1 className="admin-title">Admin Portal</h1>
        </div>
        
        <div className="header-right">
          <div className="user-info">
            <span className="user-name">{adminUser?.name}</span>
            <span className="user-role">{adminUser?.role || 'Admin'}</span>
          </div>
          <button onClick={handleLogout} className="logout-button">
            🚪 Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {/* Sidebar Navigation */}
        <nav className={`admin-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-content">
            <div className="nav-section">
              <h3>Dashboard</h3>
              <ul>
                <li>
                  <a 
                    href="/admin/dashboard" 
                    className={isActivePage('/admin/dashboard') ? 'active' : ''}
                    onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }}
                  >
                    📊 Overview
                  </a>
                </li>
              </ul>
            </div>

            <div className="nav-section">
              <h3>Tenant Management</h3>
              <ul>
                <li>
                  <a 
                    href="/admin/tenants" 
                    className={isActivePage('/admin/tenants') ? 'active' : ''}
                    onClick={(e) => { e.preventDefault(); navigate('/admin/tenants'); }}
                  >
                    🏢 All Tenants
                  </a>
                </li>
                {hasPermission('can_manage_tenants') && (
                  <li>
                    <a 
                      href="/admin/provision-tenant" 
                      className={isActivePage('/admin/provision-tenant') ? 'active' : ''}
                      onClick={(e) => { e.preventDefault(); navigate('/admin/provision-tenant'); }}
                    >
                      ➕ Create Tenant
                    </a>
                  </li>
                )}
                {hasPermission('can_manage_migrations') && (
                  <li>
                    <a 
                      href="/admin/migrations" 
                      className={isActivePage('/admin/migrations') ? 'active' : ''}
                      onClick={(e) => { e.preventDefault(); navigate('/admin/migrations'); }}
                    >
                      🔄 Migrations
                    </a>
                  </li>
                )}
              </ul>
            </div>

            <div className="nav-section">
              <h3>User Management</h3>
              <ul>
                <li>
                  <a 
                    href="/admin/users" 
                    className={isActivePage('/admin/users') ? 'active' : ''}
                    onClick={(e) => { e.preventDefault(); navigate('/admin/users'); }}
                  >
                    👥 User Invitations
                  </a>
                </li>
                {hasPermission('can_impersonate_users') && (
                  <li>
                    <a 
                      href="/admin/impersonation" 
                      className={isActivePage('/admin/impersonation') ? 'active' : ''}
                      onClick={(e) => { e.preventDefault(); navigate('/admin/impersonation'); }}
                    >
                      👤 User Impersonation
                    </a>
                  </li>
                )}
              </ul>
            </div>

            <div className="nav-section">
              <h3>Data Management</h3>
              <ul>
                <li>
                  <a 
                    href="/admin/sync" 
                    className={isActivePage('/admin/sync') ? 'active' : ''}
                    onClick={(e) => { e.preventDefault(); navigate('/admin/sync'); }}
                  >
                    🔄 Sync Management
                  </a>
                </li>
              </ul>
            </div>

            <div className="nav-section">
              <h3>System</h3>
              <ul>
                <li>
                  <a 
                    href="/admin/activity" 
                    className={isActivePage('/admin/activity') ? 'active' : ''}
                    onClick={(e) => { e.preventDefault(); navigate('/admin/activity'); }}
                  >
                    📋 Activity Logs
                  </a>
                </li>
                <li>
                  <a 
                    href="/admin/monitoring" 
                    className={isActivePage('/admin/monitoring') ? 'active' : ''}
                    onClick={(e) => { e.preventDefault(); navigate('/admin/monitoring'); }}
                  >
                    📡 System Monitoring
                  </a>
                </li>
                {hasPermission('can_manage_system_settings') && (
                  <li>
                    <a 
                      href="/admin/settings" 
                      className={isActivePage('/admin/settings') ? 'active' : ''}
                      onClick={(e) => { e.preventDefault(); navigate('/admin/settings'); }}
                    >
                      ⚙️ Settings
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </nav>

        {/* Main Content Area */}
        <main className="admin-main">
          <div className="main-content">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout; 